import React from 'react'

import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'

import App from './App'
import { __DEV__, appConfig } from 'appConfig'

import messages from 'i18n/locales/en.json'
import compiledMessages from 'i18n/compiled/en.json'
import { createBrowserHistory } from 'history'

import 'styles/index.css'

import { captureLeadIdFromUrl } from 'libs/attribution'
import { IGNORED_RESOURCE_SPAN_OPS, isOwnRequest } from 'libs/sentry'

// WEB-1046: capture the attribution lead_id from the entry URL (put there by
// the zoe.com → quiz redirect) before anything can mutate the location.
captureLeadIdFromUrl()

const history = createBrowserHistory()

Sentry.init({
  dsn: appConfig.sentryDsn,
  environment: appConfig.environment,
  normalizeDepth: 5,
  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({
      history,
      shouldCreateSpanForRequest: isOwnRequest,
      ignoreResourceSpans: IGNORED_RESOURCE_SPAN_OPS,
      // Marks and measures here come from third-party scripts, not our code.
      ignorePerformanceApiSpans: [/./],
      // Long-frame spans cost a span per janky frame; web vitals already report INP.
      enableLongTask: false,
      enableLongAnimationFrame: false,
    }),
  ],
  tracesSampleRate: 0.2,
  tracePropagationTargets: ['localhost', /^https:\/\/quiz\.services\.(prod|stage)\.zoe\.net/],
  ignoreErrors: [
    // GTM/GA errors
    /Can't find variable: (_tfa|twq|ttq|PaymentAutofillConfig)/,
    /(_tfa|twq|ttq) is not defined/,
    // Browser / browser extension errors
    'Extension context invalidated.',
    /.*@webkit-masked-url.*/,
    // WEB-1279: bridge errors from the Meta in-app browser's injected script
    /^Error invoking postMessage: /,
    // google analytic errors
    'CustomEvent: Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
    'Event `XMLHttpRequestProgressEvent` (type=error) captured as promise rejection',
    'Event `ProgressEvent` (type=error) captured as promise rejection',
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Ignore Google flakiness
    /\/(gtm|ga|analytics)\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    // Firefox extensions
    /^resource:\/\//i,
    // Other plugins
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    // Cookie consent
    /cdn\.cookielaw\.org/i,
    /cookiehub/i,
    /scripttemplates\//i,
  ],
})

const root = createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <Sentry.ErrorBoundary fallback={<p>An error has occured</p>}>
    <React.StrictMode>
      <App locale="en" messages={__DEV__ ? messages : compiledMessages} />
    </React.StrictMode>
  </Sentry.ErrorBoundary>,
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// import reportWebVitals from "./reportWebVitals";
// reportWebVitals();
